import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, Mail, Send, AlertCircle, Check, X, User, Calendar, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function PendingPlayers() {
  const [pendingPlayers, setPendingPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sendingInvite, setSendingInvite] = useState(null);

  useEffect(() => {
    loadPendingPlayers();
  }, []);

  const loadPendingPlayers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children/pending-invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPlayers(response.data);
    } catch (error) {
      console.error('Failed to load pending players:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (player) => {
    setEditingPlayer(player.id);
    setEditForm({
      parent_email: player.parent_email || '',
      parent_name: player.parent_name || '',
      date_of_birth: player.date_of_birth ? player.date_of_birth.split('T')[0] : '2010-01-01',
      gender: player.gender || 'male'
    });
  };

  const cancelEditing = () => {
    setEditingPlayer(null);
    setEditForm({});
  };

  const savePlayerDetails = async (playerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/children/${playerId}/update-profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Player details updated');
      setEditingPlayer(null);
      loadPendingPlayers();
    } catch (error) {
      toast.error('Failed to update player');
    }
  };

  const sendInvitation = async (playerId) => {
    const player = pendingPlayers.find(p => p.id === playerId);
    if (!player.parent_email) {
      toast.error('Please add parent email first');
      return;
    }

    setSendingInvite(playerId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/${playerId}/send-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation sent successfully');
      loadPendingPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
        <div className="flex items-center gap-3 mb-4">
          <UserPlus className="h-5 w-5 text-[#ff025f]" />
          <h2 className="text-lg font-bold text-white">Pending Players</h2>
        </div>
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (pendingPlayers.length === 0) {
    return (
      <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
        <div className="flex items-center gap-3 mb-4">
          <UserPlus className="h-5 w-5 text-[#02b5ff]" />
          <h2 className="text-lg font-bold text-white">Pending Players</h2>
        </div>
        <div className="text-center py-8">
          <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <p className="text-zinc-400">All players have been invited!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800" data-testid="pending-players-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#ff025f]/20 to-[#ff025f]/10 rounded-xl flex items-center justify-center">
            <UserPlus className="h-5 w-5 text-[#ff025f]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Pending Players</h2>
            <p className="text-xs text-zinc-500">{pendingPlayers.length} players need email & invitation</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#ff025f]/10 rounded-full">
          <AlertCircle className="h-4 w-4 text-[#ff025f]" />
          <span className="text-sm font-bold text-[#ff025f]">{pendingPlayers.length}</span>
        </div>
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {pendingPlayers.map((player) => (
          <div
            key={player.id}
            className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-zinc-600 transition-all"
            data-testid={`pending-player-${player.id}`}
          >
            {editingPlayer === player.id ? (
              /* Edit Mode */
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-4 w-4 text-[#02b5ff]" />
                  <span className="font-bold text-white">{player.first_name} {player.last_name}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Parent Email *</label>
                    <Input
                      type="email"
                      placeholder="parent@email.com"
                      value={editForm.parent_email}
                      onChange={(e) => setEditForm({ ...editForm, parent_email: e.target.value })}
                      className="h-9 bg-zinc-700 border-zinc-600 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Parent Name</label>
                    <Input
                      placeholder="Parent name"
                      value={editForm.parent_name}
                      onChange={(e) => setEditForm({ ...editForm, parent_name: e.target.value })}
                      className="h-9 bg-zinc-700 border-zinc-600 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Date of Birth</label>
                    <Input
                      type="date"
                      value={editForm.date_of_birth}
                      onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                      className="h-9 bg-zinc-700 border-zinc-600 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Gender</label>
                    <select
                      value={editForm.gender}
                      onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                      className="h-9 w-full bg-zinc-700 border border-zinc-600 rounded-md text-sm text-white px-3"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelEditing}
                    className="h-8 border-zinc-600 text-zinc-400 hover:text-white"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => savePlayerDetails(player.id)}
                    className="h-8 bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-zinc-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{player.first_name} {player.last_name}</h3>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      {player.parent_email ? (
                        <span className="flex items-center gap-1 text-green-400">
                          <Mail className="h-3 w-3" />
                          {player.parent_email}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[#ff025f]">
                          <AlertCircle className="h-3 w-3" />
                          No email set
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEditing(player)}
                    className="h-8 border-zinc-600 text-zinc-400 hover:text-white hover:border-[#02b5ff]"
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendInvitation(player.id)}
                    disabled={!player.parent_email || sendingInvite === player.id}
                    className={`h-8 ${
                      player.parent_email 
                        ? 'bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white' 
                        : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                    }`}
                  >
                    {sendingInvite === player.id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="h-3 w-3 mr-1" />
                        Invite
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
